import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PhoneOutgoing, Plus, ArrowLeft, Play, Loader2, Pencil, AlertTriangle,
  PhoneCall, CalendarCheck, Activity, Sparkles, ListChecks, Ban, FileText,
  Copy, Timer,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

import {
  OUTCOME_TONE, MetricCard, ConfigRow, StatusBadge, fmtDate,
} from "./ai_outbound/shared";
import { TemplatesPanel } from "./ai_outbound/TemplatesPanel";
import { AnalyticsPanel } from "./ai_outbound/AnalyticsPanel";
import { CalendlyConnectPill } from "./ai_outbound/CalendlyConnectPill";
import { ForkCampaignButton } from "./ai_outbound/ForkCampaignDialog";
import { SeeCopiesButton } from "./ai_outbound/SeeCopiesButton";

import { CampaignInsightsPanel } from "./ai_outbound/insights";
import { CrossCampaignInsightsPanel } from "./ai_outbound/crossCampaign";
import { DigestConfigPanel, DigestHistoryPanel } from "./ai_outbound/digest";
import { RecentTwilioCallsStrip } from "./ai_outbound/recentCalls";
import {
  TestCallButton, OutboundPauseButton, ExportCallHistoryButton, BulkRunButton,
} from "./ai_outbound/quickActions";
import { CampaignsList } from "./ai_outbound/campaignsList";
import { CampaignBuilderDialog } from "./ai_outbound/campaignBuilder";
import { RecipientListsPanel, DncListsPanel } from "./ai_outbound/recipientLists";
import { usePageBreadcrumb } from "@/context/BreadcrumbContext";

export default function AIOutbound() {
  const { campaignId } = useParams();
  if (campaignId) return <CampaignDetail campaignId={campaignId} />;
  return <CampaignsHome />;
}

function CampaignsHome() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("campaigns");
  const [statusFilter, setStatusFilter] = usePersistedState("ai-outbound.status", "active");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [dncLists, setDncLists] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        api.get(`/outbound/campaigns?status_filter=${statusFilter}`),
        api.get("/outbound/lists"),
      ]);
      setCampaigns(c.data.campaigns || []);
      setLists(l.data.lists || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const openEdit = async (cid) => {
    try {
      const { data } = await api.get(`/outbound/campaigns/${cid}`);
      setEditingCampaign(data);
      setBuilderOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadDncLists = async () => {
    try { const { data } = await api.get("/outbound/dnc-lists"); setDncLists(data.lists || []); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { loadDncLists(); }, []);

  return (
    <div>
      <PageHeader testid="ai-outbound-header" title="UC AI Outbound"
        subtitle="AI-powered outbound calling campaigns. Real dialer + APScheduler orchestrator online — opt each campaign in via Auto-dial."
        icon={PhoneOutgoing}>
        <OutboundPauseButton />
        <ExportCallHistoryButton />
        <BulkRunButton onDone={load} />
        <Button data-testid="ai-outbound-new-campaign-btn" onClick={() => { setEditingCampaign(null); setBuilderOpen(true); }}
          className="bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus className="h-4 w-4" /> New campaign
        </Button>
      </PageHeader>

      <div className="mb-3 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-700"
        data-testid="ai-outbound-mock-banner">
        <Sparkles className="h-3 w-3" /> Mocked · No live calls placed. Recipient lists and configuration are real.
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="campaigns" data-testid="ai-outbound-tab-campaigns"><PhoneCall className="h-4 w-4" /> Campaigns</TabsTrigger>
          <TabsTrigger value="lists" data-testid="ai-outbound-tab-lists"><ListChecks className="h-4 w-4" /> Recipient Lists</TabsTrigger>
          <TabsTrigger value="dnc" data-testid="ai-outbound-tab-dnc"><Ban className="h-4 w-4" /> DNC Lists</TabsTrigger>
          <TabsTrigger value="templates" data-testid="ai-outbound-tab-templates"><FileText className="h-4 w-4" /> Templates</TabsTrigger>
          <TabsTrigger value="insights" data-testid="ai-outbound-tab-insights"><Sparkles className="h-4 w-4" /> Insights</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="ai-outbound-tab-analytics"><Activity className="h-4 w-4" /> Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns">
          <CampaignsList campaigns={campaigns} loading={loading} statusFilter={statusFilter} onStatusFilterChange={setStatusFilter}
            onOpen={(id) => navigate(`/ai-outbound/${id}`)} onEdit={openEdit} onReload={load} />
        </TabsContent>
        <TabsContent value="lists">
          <RecipientListsPanel lists={lists} onReload={load} />
        </TabsContent>
        <TabsContent value="dnc">
          <DncListsPanel lists={dncLists} onReload={loadDncLists} />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesPanel />
        </TabsContent>
        <TabsContent value="insights">
          <div className="space-y-4">
            <CrossCampaignInsightsPanel onOpenCampaign={(id) => navigate(`/ai-outbound/${id}`)} />
            <DigestConfigPanel />
            <DigestHistoryPanel />
          </div>
        </TabsContent>
        <TabsContent value="analytics">
          <AnalyticsPanel campaigns={campaigns} />
        </TabsContent>
      </Tabs>

      <CampaignBuilderDialog
        open={builderOpen}
        onClose={() => { setBuilderOpen(false); setEditingCampaign(null); }}
        lists={lists}
        dncLists={dncLists}
        campaign={editingCampaign}
        onSaved={(id) => { setBuilderOpen(false); setEditingCampaign(null); load(); if (!editingCampaign) navigate(`/ai-outbound/${id}`); }} />
    </div>
  );
}

function CampaignDetail({ campaignId }) {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [running, setRunning] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [lists, setLists] = useState([]);
  const [dncLists, setDncLists] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    try {
      const [cr, ar, an, al, ls, dl] = await Promise.all([
        api.get(`/outbound/campaigns/${campaignId}`),
        api.get(`/outbound/campaigns/${campaignId}/attempts`),
        api.get(`/outbound/analytics`, { params: { campaign_id: campaignId } }),
        api.get(`/outbound/campaigns/${campaignId}/alerts`),
        api.get(`/outbound/lists`),
        api.get(`/outbound/dnc-lists`),
      ]);
      setC(cr.data); setAttempts(ar.data.attempts || []); setAnalytics(an.data);
      setAlerts(al.data.alerts || []); setLists(ls.data.lists || []);
      setDncLists(dl.data.lists || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/ai-outbound");
    }
  };
  useEffect(() => { load(); }, [campaignId]);

  usePageBreadcrumb(useMemo(() => (c ? [
    { label: "AI Outbound", to: "/ai-outbound" },
    { label: c.name || "Campaign" },
  ] : null), [c]));

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/outbound/campaigns/${campaignId}/start`);
      const alertMsg = data.alerts_fired ? ` · ${data.alerts_fired} alert(s) fired` : "";
      toast.success(`Mock run complete · ${data.attempts} attempt(s)${alertMsg}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const dismissAlert = async (a) => {
    try {
      await api.post(`/outbound/campaigns/${campaignId}/alerts/${a.alert_id}/dismiss`);
      setAlerts((cur) => cur.filter((x) => x.alert_id !== a.alert_id));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!c) return <div className="text-slate-400" data-testid="ai-outbound-detail-loading">Loading…</div>;

  const subtitleBase = `List: ${c.list_name || "—"} · ${c.tone} tone${c.voice_name ? ` · ${c.voice_name}` : ""}`;

  return (
    <div>
      <PageHeader testid="ai-outbound-detail-header" title={c.name} subtitle={subtitleBase} icon={PhoneOutgoing}
        titleAccessory={c.forked_from_campaign_id ? (
          <button
            type="button"
            onClick={() => navigate(`/ai-outbound/${c.forked_from_campaign_id}`)}
            data-testid="ai-outbound-forked-from-badge"
            title={`Open the source campaign${c.forked_from_org_name ? ` in ${c.forked_from_org_name}` : ""}`}
            className="ml-2 inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 transition-colors hover:bg-blue-100 hover:border-blue-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
          >
            <Copy className="h-3 w-3" />
            <span>Copied from</span>
            <span className="font-semibold">
              {c.forked_from_org_name || c.forked_from_campaign_name || "another campaign"}
            </span>
          </button>
        ) : null}
      >
        <Button variant="outline" onClick={() => navigate("/ai-outbound")} data-testid="ai-outbound-back-btn"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <label className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium" data-testid="ai-outbound-detail-active-toggle">
          <Switch checked={c.active !== false}
            data-testid="ai-outbound-detail-active-switch"
            onCheckedChange={async (v) => {
              try {
                await api.patch(`/outbound/campaigns/${campaignId}`, { active: v });
                toast.success(`Campaign ${v ? "activated" : "paused"}`);
                load();
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            }} />
          {c.active !== false ? "Active" : "Inactive"}
        </label>
        <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="ai-outbound-edit-detail-btn"><Pencil className="h-4 w-4" /> Edit</Button>
        <ForkCampaignButton campaign={c} />
        <SeeCopiesButton campaign={c} />
        <CalendlyConnectPill nextPath={`/ai-outbound/${campaignId}`} />
        <Button data-testid="ai-outbound-mock-run-btn" onClick={run} disabled={running || c.active === false}
          className="bg-primary hover:bg-primary/90 text-primary-foreground">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {running ? "Running mock…" : (c.active === false ? "Inactive — cannot run" : "Run mock")}
        </Button>
        <TestCallButton campaignName={c.name} />
      </PageHeader>

      {alerts.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="ai-outbound-alerts-banner">
          {alerts.map((a) => {
            const tone = a.severity === "critical"
              ? "border-red-200 bg-red-50 text-red-800"
              : "border-amber-200 bg-amber-50 text-amber-800";
            return (
              <div key={a.alert_id} data-testid={`ai-outbound-alert-${a.alert_id}`}
                className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 ${tone}`}>
                <div className="flex min-w-0 items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{a.label}</div>
                    <div className="text-xs opacity-90">{a.message}</div>
                    <div className="mt-0.5 text-[11px] opacity-60">Fired {fmtDate(a.created_at)}</div>
                  </div>
                </div>
                <button onClick={() => dismissAlert(a)} data-testid={`ai-outbound-alert-dismiss-${a.alert_id}`}
                  className="shrink-0 rounded px-2 py-1 text-xs font-medium hover:bg-white/60">Dismiss</button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4" data-testid="ai-outbound-detail-metrics">
        <MetricCard icon={PhoneCall} label="Attempts" value={analytics?.totals?.recipients_called ?? 0} />
        <MetricCard icon={CalendarCheck} label="Booked" value={analytics?.totals?.booked ?? 0} tone="emerald" />
        <MetricCard icon={Activity} label="Connect rate" value={`${analytics?.totals?.connect_rate ?? 0}%`} tone="sky" />
        <MetricCard icon={Timer} label="Total minutes" value={`${Number(analytics?.totals?.total_minutes || 0).toFixed(1)}`} tone="amber" />
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="border border-slate-200 p-4">
          <div className="mb-2 font-heading text-sm font-semibold text-slate-800">Configuration</div>
          <dl className="space-y-1 text-xs text-slate-600">
            <ConfigRow k="Status" v={<span className="flex items-center gap-2"><StatusBadge status={c.status} />
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${c.active !== false
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-slate-300 bg-slate-100 text-slate-500"}`}>
                {c.active !== false ? "Active" : "Inactive"}
              </span></span>} />
            <ConfigRow k="Voice" v={c.voice_name || <span className="text-slate-400">Default</span>} />
            <ConfigRow k="Tone" v={c.tone} />
            <ConfigRow k="Calendly" v={c.calendly_link ? <a href={c.calendly_link} target="_blank" rel="noreferrer" className="text-primary underline">{c.calendly_link}</a> : "—"} />
            <ConfigRow k="Retries" v={`${c.retry_policy?.max_retries ?? 0} · ${c.retry_policy?.gap_hours ?? 0}h gap`} />
            <ConfigRow k="Call window" v={`${c.tz_window?.start_hour ?? 9}:00 – ${c.tz_window?.end_hour ?? 18}:00`} />
            <ConfigRow k="DNC" v={c.dnc_enabled ? (
              (c.dnc_list_ids || []).length > 0
                ? (c.dnc_list_ids || []).map((id) => dncLists.find((d) => d.dnc_list_id === id)?.name || id).join(", ")
                : "Enabled, no lists"
            ) : "Off"} />
            <ConfigRow k="Minutes cap" v={`${Number(c.minutes_cap || 0)} min`} />
            <ConfigRow k="Alerts" v={c.alerts?.enabled ? `Warn ${c.alerts.minutes_cap_warn_pct ?? 80}% · floor ${c.alerts.book_rate_floor_pct ?? 5}%${c.alerts.email_owner ? " · email" : ""}` : "Off"} />
            <ConfigRow k="Q&A snippets" v={(c.qa_snippets || []).length} />
          </dl>
        </Card>
        <Card className="border border-slate-200 p-4">
          <div className="mb-2 font-heading text-sm font-semibold text-slate-800">Prompt</div>
          <div className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs text-slate-700">{c.prompt || <span className="text-slate-400">No prompt set — click Edit to write your opening.</span>}</div>
        </Card>
      </div>

      <div className="mb-4">
        <CampaignInsightsPanel campaignId={c.campaign_id} />
      </div>

      <div className="mb-4">
        <RecentTwilioCallsStrip />
      </div>

      <Card className="border border-slate-200 p-0 overflow-hidden" data-testid="ai-outbound-attempts-table">
        <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800">Call attempts ({attempts.length})</div>
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Recipient</th>
                <th className="px-3 py-2 text-left">Outcome</th>
                <th className="px-3 py-2 text-left">Sentiment</th>
                <th className="px-3 py-2 text-right">Duration</th>
                <th className="px-3 py-2 text-right">Minutes</th>
                <th className="px-3 py-2 text-left">Started</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {attempts.map((a) => (
                <tr key={a.attempt_id} data-testid={`ai-outbound-attempt-${a.attempt_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-700">{a.recipient_name || a.recipient_phone || "—"}</div>
                    <div className="text-[11px] text-slate-400">{a.recipient_phone}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${OUTCOME_TONE[a.outcome] || "bg-slate-100 border-slate-200 text-slate-600"}`}>
                      {a.outcome.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-600 capitalize">{a.sentiment}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{a.duration_sec}s</td>
                  <td className="px-3 py-2 text-right text-slate-700">{(Number(a.duration_sec || 0) / 60).toFixed(2)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(a.started_at)}</td>
                </tr>
              ))}
              {attempts.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-xs text-slate-400">No attempts yet — click &ldquo;Run mock&rdquo; to simulate this campaign.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <CampaignBuilderDialog open={editOpen} onClose={() => setEditOpen(false)} lists={lists} dncLists={dncLists} campaign={c}
        onSaved={() => { setEditOpen(false); load(); }} />
    </div>
  );
}
